import React, { useCallback, useMemo } from 'react'
import styles from 'modules/Services/Account/network-account.module.scss'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import difference from 'lodash/difference'
import { notificationShow } from 'modules/Notifications/actions'
import useWriteToClipboard from 'utils/hooks/useWriteToClipboard'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { Button, Grid, Icon, Tooltip, Typography } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import ActionLink from 'modules/Services/Account/subcomponents/ActionLink'
import DropdownComponent from 'components/Dropdown'
import PropTypes from 'prop-types'

const LeftSideActions = ({ actions, accountId, networkType }) => {
  const { t } = useTranslation()
  const writeToClipboard = useWriteToClipboard()
  const dispatch = useDispatch()
  const breakpoints = useBreakpoint()
  const pathGen = useGeneratePathI18n()

  const {
    networkAccount: {
      office_url,
      login,
      client_id,
      auto_replenishment,
      balance,
      main_action,
      advertiser_info,
      name,
    },
    i18n,
  } = useSelector(
    (state) => ({
      networkAccount: state['services/network-account'],
      i18n: state['app'].i18n,
    }),
    shallowEqual,
  )

  const leftSideActions = useMemo(
    () => difference(actions, [main_action, 'deposit', 'refund', 'auto_replenish']),
    [main_action, actions],
  )

  const toClipboard = useCallback((value) => {
    writeToClipboard(value)

    dispatch(
      notificationShow({
        type: 'success',
        message: 'common::notifications.success_copy_notification',
      }),
    )
  }, [])

  const collapseLeftAction = useMemo(() => {
    const hasAdvertiserInfo = advertiser_info !== null

    const { md, fb, lg, xl, mdl, xxl } = breakpoints

    if (!md) {
      return hasAdvertiserInfo || leftSideActions.length > 2
    } else if (!fb) {
      return false
    } else if (!lg) {
      return hasAdvertiserInfo || leftSideActions.length > 2
    } else if (!xl) {
      return leftSideActions.length > 1 && hasAdvertiserInfo
    } else if (!mdl) {
      return false
    } else if (!xxl) {
      return leftSideActions.length > 2 && hasAdvertiserInfo
    } else {
      return false
    }
  }, [breakpoints, leftSideActions, advertiser_info])

  const collapseDelete = useMemo(() => {
    const hasAdvertiserInfo = advertiser_info !== null
    const { md, fb, fhd } = breakpoints

    if (!md) {
      return hasAdvertiserInfo || leftSideActions.length > 1
    } else if (!fb) {
      return false
    } else if (!fhd) {
      return leftSideActions.length > 1
    } else {
      return false
    }
  }, [breakpoints, advertiser_info])

  return (
    <Grid
      className={styles['network-account__login-container']}
      alignItems={'center'}
      gap={16}
      wrap={breakpoints.lg}
    >
      {!collapseLeftAction && office_url?.length > 0 && (
        <Button
          grey
          size={'s'}
          variant={'secondary'}
          startIcon={
            <Icon
              name={'login'}
              color={'inherit'}
            />
          }
          component={'a'}
          target={'_blank'}
          href={office_url}
        >
          {t('services::account.login_button_label')}
        </Button>
      )}

      {advertiser_info !== null && (
        <Button
          className={styles['network-account__advertiser-info-button']}
          fullWidth={!breakpoints.sm}
          component={Link}
          to={pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT_ADVERTISER_INFO_EDIT, {
            i18n,
            networkType,
            accountId,
          })}
          variant={'secondary'}
          size={'s'}
          grey
          noWrap
          startIcon={
            <Icon
              name={'profile'}
              color={'inherit'}
            />
          }
          endIcon={
            advertiser_info?.status !== 'valid' ? (
              <Tooltip
                title={t('services::account_list.advertiser_info_not_full_filled.tooltip.text')}
                placement="top"
                leaveDelay={250}
                arrow
              >
                <Icon
                  name={'status warning2'}
                  size={'sm'}
                  color={'Label-Error-Primary'}
                />
              </Tooltip>
            ) : null
          }
        >
          {t('services::account.tax_info_button_label')}
        </Button>
      )}

      {main_action && networkType === 'yandex-business' && (
        <ActionLink
          action={'campaigns'}
          i18n={i18n}
          id={accountId}
          type={networkType}
        />
      )}

      {!collapseLeftAction ? (
        leftSideActions.map((action, i) => (
          <ActionLink
            noLabel={
              ['delete', 'archive'].includes(action)
                ? collapseDelete
                : leftSideActions.length > 1 && i === leftSideActions.length - 1
            }
            key={'action-link-' + i}
            action={action}
            i18n={i18n}
            id={accountId}
            type={networkType}
            balance={balance}
            auto_replenishment={auto_replenishment}
          />
        ))
      ) : (
        <DropdownComponent
          wrapItems
          itemsSize={'m'}
          ButtonComponent={() => (
            <Button
              type={'button'}
              size={'s'}
              variant={'ghost'}
              grey
              noLabel
              startIcon={<Icon name={'other'} />}
            />
          )}
        >
          {office_url?.length > 0 && (
            <Button
              grey
              size={'s'}
              variant={'ghost'}
              link
              startIcon={
                <Icon
                  name={'login'}
                  color={'inherit'}
                />
              }
              component={'a'}
              target={'_blank'}
              href={office_url}
            >
              {t('services::account.login_button_label')}
            </Button>
          )}

          {leftSideActions.map((action, i) => (
            <ActionLink
              noLabel={false}
              key={'action-link-' + i}
              action={action}
              i18n={i18n}
              id={accountId}
              type={networkType}
              balance={balance}
              auto_replenishment={auto_replenishment}
              name={name ?? login}
            />
          ))}
        </DropdownComponent>
      )}

      {networkType === 'googleads' && client_id && (
        <Button
          onClick={() => toClipboard(client_id)}
          className={styles['network-account__cid']}
          size={'s'}
          variant={'primary'}
          grey
          type={'button'}
          endIcon={
            <Icon
              name={'copy-link'}
              className={styles['network-account__cid-copy-icon']}
            />
          }
        >
          <Typography
            className={styles['network-account__logo']}
            variant="Body-15"
            component={'span'}
          >
            {t('services::account.cid_label')}: {` `}
            {client_id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
          </Typography>
        </Button>
      )}
    </Grid>
  )
}

export default LeftSideActions

LeftSideActions.propTypes = {
  actions: PropTypes.array,
  accountId: PropTypes.string,
  networkType: PropTypes.string,
}
